import { useEffect, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { useLayoutContext } from "@/components/Layout";
import { useUsageSummary, useUsageSavings } from "@/api/hooks/use-usage";
import { useRoutingMetrics } from "@/api/hooks/use-routing";
import { useTrialStatus } from "@/api/hooks/use-billing-trial";
import { KpiCard } from "@/components/KpiCard";
import { KpiGrid } from "@/components/KpiGrid";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { useTheme } from "@/contexts/ThemeContext";
import {
  formatUsd,
  formatCount,
  formatLatency,
  formatPercent,
  formatTokens,
} from "@/lib/format";

interface ChartTheme {
  grid: string;
  axis: string;
  tick: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  accent: string;
  accentSoft: string;
}

function readChartTheme(): ChartTheme {
  if (typeof window === "undefined") {
    return {
      grid: "#334155",
      axis: "#334155",
      tick: "#94A3B8",
      tooltipBg: "#111827",
      tooltipBorder: "#334155",
      tooltipText: "#94A3B8",
      accent: "#2563EB",
      accentSoft: "rgba(37, 99, 235, 0.18)",
    };
  }
  const css = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) =>
    css.getPropertyValue(name).trim() || fallback;
  return {
    grid: read("--line", "#334155"),
    axis: read("--line", "#334155"),
    tick: read("--fg-muted", "#94A3B8"),
    tooltipBg: read("--surface", "#111827"),
    tooltipBorder: read("--line", "#334155"),
    tooltipText: read("--fg-muted", "#94A3B8"),
    accent: "#2563EB",
    accentSoft: "rgba(37, 99, 235, 0.18)",
  };
}

function useChartTheme(): ChartTheme {
  const { theme } = useTheme();
  const [palette, setPalette] = useState<ChartTheme>(() => readChartTheme());
  useEffect(() => {
    // Wait for CSS transition tick so vars are settled
    const id = window.setTimeout(() => setPalette(readChartTheme()), 0);
    return () => window.clearTimeout(id);
  }, [theme]);
  return palette;
}

export function Overview() {
  const { tenantId, range, userId } = useLayoutContext();

  const savingsQ = useUsageSavings(tenantId, range, userId);
  const metricsQ = useRoutingMetrics(tenantId, range, userId);
  const dailyQ = useUsageSummary(tenantId, range, "day", userId);
  const featureQ = useUsageSummary(tenantId, range, "feature", userId);
  const userQ = useUsageSummary(tenantId, range, "user", userId);
  // Trial status is fetched independently (not in the load/error gate) so the
  // Overview never blocks on the billing endpoint; the savings card just
  // defaults to the verified view until it resolves.
  const trialQ = useTrialStatus(tenantId);

  if (!tenantId) {
    return <EmptyState message="Enter a Tenant ID in the sidebar to get started" />;
  }

  const isLoading =
    savingsQ.isLoading || metricsQ.isLoading || dailyQ.isLoading;
  const hasError = savingsQ.isError || metricsQ.isError || dailyQ.isError;

  if (isLoading) {
    return <LoadingSkeleton cards={9} rows={5} />;
  }

  if (hasError) {
    const msg =
      savingsQ.error?.message ||
      metricsQ.error?.message ||
      dailyQ.error?.message ||
      "Failed to load overview data";
    return (
      <ErrorState
        message={msg}
        onRetry={() => {
          savingsQ.refetch();
          metricsQ.refetch();
          dailyQ.refetch();
          featureQ.refetch();
          userQ.refetch();
        }}
      />
    );
  }

  const savings = savingsQ.data!;
  const metrics = metricsQ.data!;
  const daily = dailyQ.data?.buckets ?? [];
  // cost arrives as a decimal string; Recharts sizes the axis by comparing
  // values, and as strings "1306.20" < "975.00", so plot numbers.
  const dailyChart = daily.map((d) => ({ ...d, cost: parseFloat(d.cost) }));

  const totalRequests = daily.reduce((sum, d) => sum + d.count, 0);
  const savingsPct = Number.parseFloat(savings.savings_pct);
  const savingsValue = `${formatUsd(savings.cost_saved)}${
    Number.isFinite(savingsPct) ? ` (${Math.round(savingsPct)}%)` : ""
  }`;

  // During an active trial the customer's real traffic runs unoptimized
  // upstream while Phantm measures the optimized cost in a parallel shadow run,
  // so "Verified Savings" (billable events only) is correctly $0. Surface the
  // measured trial number instead — the same figure the trial banner shows.
  const trial = trialQ.data;
  const isActiveTrial = trial?.trial_status === "active";
  // Mirror the verified-savings format ("$X (Y%)"); only append the percent
  // once at least one shadow run has produced a real number.
  const trialSavingsValue = `${formatUsd(trial?.projected_savings_usd ?? "0")}${
    trial && trial.shadowed_request_count > 0
      ? ` (${Math.round(trial.projected_savings_pct * 100)}%)`
      : ""
  }`;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">
            Overview
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            Live gateway metrics, savings, and routing performance.
          </p>
        </div>
        <div className="hidden items-center gap-2 rounded-full border border-line-subtle bg-surface px-3 py-1 text-xs text-fg-muted md:inline-flex">
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-full bg-safe-green shadow-[0_0_6px_var(--color-safe-green)] animate-pulse-dot"
          />
          Gateway live
        </div>
      </div>

      {/* KPI row */}
      <KpiGrid>
        <KpiCard label="Actual Spend" value={formatUsd(savings.cost_actual)} />
        <KpiCard label="Without Phantm" value={formatUsd(savings.cost_requested)} />
        {isActiveTrial ? (
          <KpiCard
            label="Projected Savings (trial)"
            value={trialSavingsValue}
            color="green"
          />
        ) : (
          <KpiCard
            label="Verified Savings"
            value={savingsValue}
            color="green"
          />
        )}
        <KpiCard label="Requests" value={formatCount(totalRequests)} />
        <KpiCard label="Avg Phantm Latency" value={formatLatency(metrics.avg_pipeline_latency_ms ?? null)} />
        <KpiCard
          label="Error Rate"
          value={metrics.total > 0 ? formatPercent(metrics.errors / metrics.total) : "0.0%"}
          color={metrics.errors > 0 ? "red" : "default"}
        />
        <KpiCard
          label="Block Rate"
          value={metrics.total > 0 ? formatPercent(metrics.blocks / metrics.total) : "0.0%"}
          color={metrics.blocks > 0 ? "amber" : "default"}
        />
        <KpiCard
          label="Cache Hit Rate"
          value={formatPercent(metrics.cache_hit_rate)}
        />
      </KpiGrid>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Spend Over Time" data={dailyChart} dataKey="cost" formatter={formatUsd} />
        <ChartCard title="Requests Over Time" data={dailyChart} dataKey="count" formatter={formatCount} />
      </div>

      {/* Breakdown tables */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BreakdownTable
          title="Top Features by Spend"
          query={featureQ}
          groupLabel="Feature"
        />
        <BreakdownTable
          title="Top Users by Spend"
          query={userQ}
          groupLabel="User"
        />
      </div>
    </div>
  );
}

// ---- internal chart component (not exported, not generic) ----

interface ChartCardProps {
  title: string;
  data: Array<{ key: string; [k: string]: unknown }>;
  dataKey: string;
  formatter: (v: number) => string;
}

function ChartCard({ title, data, dataKey, formatter }: ChartCardProps) {
  const palette = useChartTheme();
  const gradientId = `chart-grad-${dataKey}`;

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-line-subtle bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-fg-muted">{title}</h2>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="group rounded-lg border border-line-subtle bg-surface p-4 transition-colors hover:border-line">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg-muted">{title}</h2>
        <span className="text-[10px] uppercase tracking-[0.14em] text-fg-faint">
          {data.length} pts
        </span>
      </div>
      <div role="img" aria-label={`${title} chart`}>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={palette.accent} stopOpacity={0.32} />
                <stop offset="100%" stopColor={palette.accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.grid}
              opacity={0.5}
              vertical={false}
            />
            <XAxis
              dataKey="key"
              tick={{ fill: palette.tick, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: palette.axis }}
            />
            <YAxis
              tick={{ fill: palette.tick, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => formatter(v)}
              width={56}
            />
            <Tooltip
              cursor={{ stroke: palette.accent, strokeOpacity: 0.4, strokeWidth: 1 }}
              contentStyle={{
                background: palette.tooltipBg,
                border: `1px solid ${palette.tooltipBorder}`,
                borderRadius: 8,
                fontSize: 12,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              }}
              labelStyle={{ color: palette.tooltipText }}
              formatter={(v: number) => [formatter(v), title]}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={palette.accent}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: palette.accent, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---- internal breakdown table (not exported, not generic) ----

interface BreakdownTableProps {
  title: string;
  query: ReturnType<typeof useUsageSummary>;
  groupLabel: string;
}

function BreakdownTable({ title, query, groupLabel }: BreakdownTableProps) {
  if (query.isLoading) {
    return (
      <div className="rounded-lg border border-line-subtle bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-fg-muted">{title}</h2>
        <LoadingSkeleton rows={5} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-lg border border-line-subtle bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-fg-muted">{title}</h2>
        <ErrorState
          message={query.error?.message ?? "Failed to load"}
          onRetry={() => query.refetch()}
        />
      </div>
    );
  }

  const rows = [...(query.data?.buckets ?? [])].sort(
    (a, b) => parseFloat(b.cost) - parseFloat(a.cost),
  );

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-line-subtle bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-fg-muted">{title}</h2>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-line-subtle bg-surface p-4">
      <h2 className="mb-3 text-sm font-medium text-fg-muted">{title}</h2>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-line-subtle text-left text-fg-muted">
            <th className="pb-2 font-medium">{groupLabel}</th>
            <th className="pb-2 font-medium text-right">Requests</th>
            <th className="pb-2 font-medium text-right">Input Tokens</th>
            <th className="pb-2 font-medium text-right">Output Tokens</th>
            <th className="pb-2 font-medium text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className="border-b border-line-subtle text-fg"
            >
              <td className="py-1.5 font-mono">{row.key || "\u2014"}</td>
              <td className="py-1.5 text-right font-mono">
                {formatCount(row.count)}
              </td>
              <td className="py-1.5 text-right font-mono">
                {formatTokens(row.input_tokens)}
              </td>
              <td className="py-1.5 text-right font-mono">
                {formatTokens(row.output_tokens)}
              </td>
              <td className="py-1.5 text-right font-mono">
                {formatUsd(row.cost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
